import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { API, IS_NATIVE, OFFLINE_DIR } from "./api";

const LS_KEY = "bnm-reader-v1";
const QUEUE_KEY = "bnm-journal-queue-v1";

export const ZONES = [
  { id: "zone1", name: "Base Camp", full: "Zone I · The Terrain You've Been Walking", blurb: "Where the walking began — naming what you've carried.", start_page: 45, gap: 6 },
  { id: "zone2", name: "Lower Slopes", full: "Zone II · Surveying the Landscape", blurb: "Getting your bearings; seeing the shape of the ground.", start_page: 75, gap: 8 },
  { id: "zone3", name: "Switchbacks", full: "Zone III · Finding New Paths", blurb: "The turns that change direction without losing the trail.", start_page: 103, gap: 11 },
  { id: "zone4", name: "Ridgeline", full: "Zone IV · Reading the Map in Real Time", blurb: "A wider view; reading the terrain as you move.", start_page: 129, gap: 14 },
  { id: "zone5", name: "Exposed Face", full: "Zone V · The Conditions of the Climb", blurb: "Open ground, honest weather, steady footing.", start_page: 156, gap: 17 },
  { id: "zone6", name: "High Meadow", full: "Zone VI · New Ground", blurb: "Room to breathe; ease arriving in the body.", start_page: 190, gap: 21 },
  { id: "zone7", name: "Summit & Descent", full: "Zone VII · Arriving", blurb: "Rhythm found — and the gentle way back down.", start_page: 206, gap: 26 },
];

export function zoneIndexForPage(page) {
  let idx = -1; // before Base Camp = Trailhead
  for (let i = 0; i < ZONES.length; i += 1) {
    if (page >= ZONES[i].start_page) idx = i;
  }
  return idx;
}

function loadId() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "null");
  } catch (e) {
    return null;
  }
}
function saveId(v) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(v)); } catch (e) {}
}
function clearId() {
  try { localStorage.removeItem(LS_KEY); } catch (e) {}
}

const authHeader = (tok) => ({ headers: { Authorization: `Bearer ${tok}` } });

export const api = {
  create: () => axios.post(`${API}/reader`).then((r) => r.data),
  restore: (trail_code) => axios.post(`${API}/reader/restore`, { trail_code }).then((r) => r.data),
  me: (tok) => axios.get(`${API}/reader/me`, authHeader(tok)).then((r) => r.data),
  saveState: (tok, state) => axios.put(`${API}/reader/state`, { state }, authHeader(tok)).then((r) => r.data),
  regenerate: (tok) => axios.post(`${API}/reader/code/regenerate`, {}, authHeader(tok)).then((r) => r.data),
  revoke: (tok) => axios.delete(`${API}/reader/code`, authHeader(tok)).then((r) => r.data),
  addJournal: (tok, entry) => axios.post(`${API}/journal`, entry, authHeader(tok)).then((r) => r.data),
  deleteAllJournal: (tok) => axios.delete(`${API}/journal`, authHeader(tok)).then((r) => r.data),
  erase: (tok) => axios.delete(`${API}/reader`, authHeader(tok)).then((r) => r.data),
  config: () =>
    (IS_NATIVE
      ? axios.get(`${OFFLINE_DIR}/trail-config.json`)
      : axios.get(`${API}/trail-config`)
    ).then((r) => r.data),
};

// Offline-tolerant journal queue: entries saved while offline are replayed
// to the backend once the device is back online.
function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch (e) { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch (e) {}
}

export function useReader(enabled) {
  const [identity, setIdentity] = useState(loadId);
  const [state, setState] = useState(null);
  const [journal, setJournal] = useState([]);
  const [hasCode, setHasCode] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const tokenRef = useRef(identity?.reader_token || null);
  const saveTimer = useRef(null);

  // Bootstrap: load existing reader or create a new anonymous one.
  useEffect(() => {
    if (!enabled || loaded) return;
    let cancelled = false;
    (async () => {
      const existing = loadId();
      if (existing?.reader_token) {
        try {
          const me = await api.me(existing.reader_token);
          if (cancelled) return;
          tokenRef.current = existing.reader_token;
          setIdentity(existing);
          setState(me.state);
          setJournal(me.journal || []);
          setHasCode(me.has_code);
          setIsReturning((me.state?.last_page || 1) > 1 || (me.state?.zones_read || []).length > 0);
          setLoaded(true);
          return;
        } catch (e) {
          clearId();
        }
      }
      try {
        const created = await api.create();
        if (cancelled) return;
        const id = { reader_id: created.reader_id, reader_token: created.reader_token };
        saveId(id);
        tokenRef.current = created.reader_token;
        setIdentity(id);
        setState(created.state);
        setJournal([]);
        setHasCode(true);
        setIsReturning(false);
        setLoaded(true);
      } catch (e) {
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, loaded]);

  const persist = useCallback((next) => {
    const tok = tokenRef.current;
    if (!tok) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { api.saveState(tok, next).catch(() => {}); }, 700);
  }, []);

  const patchState = useCallback((patch) => {
    setState((prev) => {
      const base = prev || {};
      const next = typeof patch === "function" ? patch(base) : { ...base, ...patch };
      persist(next);
      return next;
    });
  }, [persist]);

  const addJournal = useCallback(async (entry) => {
    const tok = tokenRef.current;
    if (!tok) return null;
    // Optimistic insert so journaling feels instant and works offline.
    const temp = { id: `tmp-${Date.now()}`, created_at: new Date().toISOString(), pending: true, ...entry };
    setJournal((j) => [...j, temp]);
    try {
      const res = await api.addJournal(tok, entry);
      setJournal((j) => j.map((e) => (e.id === temp.id ? { id: res.id, created_at: res.created_at, ...entry } : e)));
      return { id: res.id, created_at: res.created_at, ...entry };
    } catch (err) {
      // Offline (or backend unreachable): queue for sync when back online.
      const q = loadQueue();
      q.push({ tempId: temp.id, entry });
      saveQueue(q);
      return temp;
    }
  }, []);

  const flushQueue = useCallback(async () => {
    const tok = tokenRef.current;
    if (!tok) return;
    let q = loadQueue();
    if (!q.length) return;
    const remaining = [];
    for (const item of q) {
      try {
        const res = await api.addJournal(tok, item.entry);
        setJournal((j) => j.map((e) => (e.id === item.tempId ? { id: res.id, created_at: res.created_at, ...item.entry, pending: false } : e)));
      } catch (err) {
        remaining.push(item);
      }
    }
    saveQueue(remaining);
  }, []);

  // Replay any queued journal entries when connectivity returns.
  useEffect(() => {
    if (!enabled) return;
    const onOnline = () => flushQueue();
    window.addEventListener("online", onOnline);
    if (navigator.onLine) flushQueue();
    return () => window.removeEventListener("online", onOnline);
  }, [enabled, loaded, flushQueue]);

  const deleteAllJournal = useCallback(async () => {
    const tok = tokenRef.current;
    if (!tok) return;
    await api.deleteAllJournal(tok);
    setJournal([]);
  }, []);

  const downloadJournalPdf = useCallback(async (sessionOnly = false) => {
    const tok = tokenRef.current;
    if (!tok) return;
    const res = await fetch(`${API}/journal/export.pdf${sessionOnly ? "?session=1" : ""}`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = sessionOnly ? "braced-no-more-session-notes.pdf" : "braced-no-more-field-journal.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }, []);

  const applyRestored = useCallback((data) => {
    const id = { reader_id: data.reader_id, reader_token: data.reader_token };
    saveId(id);
    tokenRef.current = data.reader_token;
    setIdentity(id);
    setState(data.state);
    setLoaded(true);
  }, []);

  const regenerate = useCallback(async () => {
    const tok = tokenRef.current;
    const res = await api.regenerate(tok);
    setHasCode(true);
    return res.trail_code;
  }, []);

  const revoke = useCallback(async () => {
    const tok = tokenRef.current;
    await api.revoke(tok);
    setHasCode(false);
  }, []);

  const erase = useCallback(async () => {
    const tok = tokenRef.current;
    try { if (tok) await api.erase(tok); } catch (e) {}
    clearId();
    tokenRef.current = null;
    setIdentity(null);
    setState(null);
    setJournal([]);
  }, []);

  return {
    identity, state, journal, hasCode, loaded, isReturning,
    token: tokenRef.current,
    patchState, addJournal, deleteAllJournal, downloadJournalPdf, applyRestored, regenerate, revoke, erase,
    setIsReturning,
  };
}
